const MAX_INPUT = 2000;

function csrfToken() {
  const meta = document.querySelector("meta[name='csrf-token']");
  return meta && meta.content;
}

function escapeHtml(value) {
  const element = document.createElement("div");
  element.textContent = value || "";
  return element.innerHTML;
}

function formatMessage(value) {
  return escapeHtml(value).replace(
    /(https?:\/\/[^\s<>"'`）)」]+)/g,
    '<a href="$1" target="_blank" rel="noopener">$1</a>'
  );
}

function initSupportChat() {
  const mount = document.getElementById("support-chat-widget");
  if (!mount || mount.dataset.initialized === "true") return;

  mount.dataset.initialized = "true";
  const labels = JSON.parse(mount.dataset.labels || "{}");
  let conversationId = null;
  let loaded = false;
  let busy = false;
  let lastUserText = null;

  mount.innerHTML = `
    <button type="button" class="support-chat__launcher" aria-label="${escapeHtml(labels.open)}">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zM7 9h10v2H7V9zm7 5H7v-2h7v2zm3-6H7V6h10v2z"/></svg>
    </button>
    <section class="support-chat__panel" aria-label="${escapeHtml(labels.title)}" aria-hidden="true">
      <header class="support-chat__header">
        <div><strong>${escapeHtml(labels.title)}</strong><small>${escapeHtml(labels.subtitle)}</small></div>
        <button type="button" class="support-chat__close" aria-label="${escapeHtml(labels.close)}">&times;</button>
      </header>
      <div class="support-chat__messages" aria-live="polite"></div>
      <div class="support-chat__composer">
        <textarea rows="1" maxlength="${MAX_INPUT}" placeholder="${escapeHtml(labels.placeholder)}"></textarea>
        <button type="button" class="support-chat__send" aria-label="${escapeHtml(labels.send)}">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z"/></svg>
        </button>
      </div>
    </section>`;

  const launcher = mount.querySelector(".support-chat__launcher");
  const panel = mount.querySelector(".support-chat__panel");
  const closeButton = mount.querySelector(".support-chat__close");
  const messagesElement = mount.querySelector(".support-chat__messages");
  const input = mount.querySelector("textarea");
  const sendButton = mount.querySelector(".support-chat__send");

  function scrollToBottom() {
    messagesElement.scrollTop = messagesElement.scrollHeight;
  }

  function addMessage(role, content, extraClass = "") {
    const message = document.createElement("div");
    message.className = `support-chat__message support-chat__message--${role} ${extraClass}`.trim();
    message.innerHTML = formatMessage(content);
    messagesElement.appendChild(message);
    scrollToBottom();
    return message;
  }

  async function request(url, options = {}) {
    const response = await fetch(url, {
      credentials: "same-origin",
      ...options,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
        ...(options.headers || {})
      }
    });
    if (!response.ok) {
      const error = new Error(`http_${response.status}`);
      error.status = response.status;
      throw error;
    }
    return response;
  }

  async function loadConversation() {
    if (loaded) return;

    const response = await request("/support_chat/conversation", { method: "GET" });
    const payload = await response.json();
    let conversation = payload.conversation;
    if (!conversation) {
      const created = await request("/support_chat/conversation", {
        method: "POST",
        body: JSON.stringify({ page_path: window.location.pathname })
      });
      conversation = (await created.json()).conversation;
    }

    conversationId = conversation.id;
    messagesElement.innerHTML = "";
    (conversation.messages || []).forEach((message) => addMessage(message.role, message.content));
    loaded = true;
  }

  function showFeedback() {
    const oldFeedback = messagesElement.querySelector(".support-chat__feedback");
    if (oldFeedback) oldFeedback.remove();

    const feedback = document.createElement("div");
    feedback.className = "support-chat__feedback";
    feedback.innerHTML = `<span>${escapeHtml(labels.resolved_question)}</span>
      <button type="button" data-action="resolve">${escapeHtml(labels.resolved)}</button>
      <button type="button" data-action="escalate">${escapeHtml(labels.unresolved)}</button>`;
    messagesElement.appendChild(feedback);
    scrollToBottom();
  }

  async function updateStatus(action) {
    await request(`/support_chat/conversation/${action}`, {
      method: "PATCH",
      body: JSON.stringify({ conversation_id: conversationId })
    });
    const feedback = messagesElement.querySelector(".support-chat__feedback");
    if (feedback) feedback.remove();
    if (action === "resolve") {
      addMessage("system", labels.resolved_message);
    } else {
      const message = addMessage("system", labels.escalated_message);
      const link = document.createElement("a");
      link.href = "mailto:support@firmee.com";
      link.textContent = labels.contact;
      message.append(document.createElement("br"), link);
    }
  }

  function showError(error) {
    const message = addMessage(
      "error",
      error.status === 429 ? labels.rate_limited : labels.error
    );
    if (error.status !== 429 && lastUserText) {
      const retry = document.createElement("button");
      retry.type = "button";
      retry.className = "support-chat__retry";
      retry.textContent = labels.retry;
      retry.addEventListener("click", () => {
        message.remove();
        sendMessage(lastUserText, false);
      });
      message.append(document.createElement("br"), retry);
    }
  }

  async function consumeEvents(response, assistantBubble) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let answer = "";
    let typing = null;

    function consumeBlock(block) {
      let eventName = "message";
      const dataLines = [];
      block.split("\n").forEach((line) => {
        if (line.startsWith("event:")) eventName = line.slice(6).trim();
        if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
      });
      if (!dataLines.length) return;

      const payload = JSON.parse(dataLines.join("\n"));
      if (eventName === "status") {
        // Handle status event - update or create typing indicator
        if (!typing) {
          typing = addMessage("system", payload.label, "support-chat__typing");
        } else {
          typing.innerHTML = formatMessage(payload.label);
          scrollToBottom();
        }
      } else if (eventName === "delta") {
        // Remove typing indicator when first delta arrives
        if (typing) {
          typing.remove();
          typing = null;
        }
        answer += payload.text || "";
        assistantBubble.innerHTML = formatMessage(answer);
        scrollToBottom();
      } else if (eventName === "done") {
        // Remove typing indicator if still present
        if (typing) {
          typing.remove();
          typing = null;
        }
        showFeedback();
      } else if (eventName === "error") {
        const error = new Error(payload.code || "upstream_error");
        throw error;
      }
    }

    while (true) {
      const result = await reader.read();
      buffer += decoder.decode(result.value || new Uint8Array(), { stream: !result.done });
      let boundary;
      while ((boundary = buffer.indexOf("\n\n")) >= 0) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        if (block.trim()) consumeBlock(block);
      }
      if (result.done) break;
    }
    if (buffer.trim()) consumeBlock(buffer);
  }

  async function sendMessage(text, addUserBubble = true) {
    if (busy || !text.trim()) return;
    busy = true;
    sendButton.disabled = true;
    lastUserText = text;
    if (addUserBubble) addMessage("user", text);
    const typing = addMessage("system", labels.thinking, "support-chat__typing");

    try {
      await loadConversation();
      const response = await request(`/support_chat/conversations/${conversationId}/messages`, {
        method: "POST",
        headers: { Accept: "text/event-stream" },
        body: JSON.stringify({ content: text })
      });
      typing.remove();
      const assistantBubble = addMessage("assistant", "");
      await consumeEvents(response, assistantBubble);
      if (!assistantBubble.textContent.trim()) assistantBubble.remove();
    } catch (error) {
      typing.remove();
      showError(error);
    } finally {
      busy = false;
      sendButton.disabled = false;
      input.focus();
    }
  }

  launcher.addEventListener("click", async () => {
    mount.classList.add("is-open");
    panel.classList.add("is-open");
    panel.setAttribute("aria-hidden", "false");
    try {
      await loadConversation();
    } catch (error) {
      showError(error);
    }
    input.focus();
  });
  closeButton.addEventListener("click", () => {
    mount.classList.remove("is-open");
    panel.classList.remove("is-open");
    panel.setAttribute("aria-hidden", "true");
    launcher.focus();
  });
  sendButton.addEventListener("click", () => {
    const text = input.value.trim();
    input.value = "";
    sendMessage(text);
  });
  input.addEventListener("keydown", (event) => {
    // IME変換確定のEnterでは送信しない（isComposing / keyCode 229 は変換中）
    if (event.isComposing || event.keyCode === 229) return;
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendButton.click();
    }
  });
  messagesElement.addEventListener("click", (event) => {
    const action = event.target.dataset && event.target.dataset.action;
    if (action) updateStatus(action).catch(showError);
  });
}

document.addEventListener("turbo:load", initSupportChat);
if (document.readyState !== "loading") initSupportChat();
